import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Button } from '@/components/ui/Button'
import { Affixed, Field, Input, Select } from '@/components/ui/Field'
import { Section } from '@/components/ui/Page'
import { describeError, supabase } from '@/lib/supabase'
import type { DueRule } from '@/lib/invoicing'
import { invoiceKeys, usePaymentPlans, type PaymentPlan } from './queries'

const RULE_LABEL: Record<DueRule, string> = {
  on_issue: 'when the invoice is issued',
  days_after_issue: 'days after the invoice',
  days_before_event: 'days before the day',
  on_event: 'on the day',
  days_after_event: 'days after the day',
}
const NEEDS_DAYS: DueRule[] = ['days_after_issue', 'days_before_event', 'days_after_event']

function describeStep(step: PaymentPlan['steps'][number]): string {
  const share = step.share === null ? 'the rest' : `${Math.round(Number(step.share) * 100)}%`
  const when = NEEDS_DAYS.includes(step.due_rule) ? `${step.offset_days} ${RULE_LABEL[step.due_rule]}` : RULE_LABEL[step.due_rule]
  return `${step.label}: ${share}, ${when}`
}

interface DraftStep {
  label: string
  share: string
  rule: DueRule
  days: string
}

/**
 * Her payment plans, as in Studio Ninja's settings. A plan is a list of
 * shares with due dates; the last share is "the rest" so every invoice adds up
 * exactly. Monthly plans for one couple are made on that couple's invoice.
 */
export function PaymentPlansPage() {
  const query = usePaymentPlans()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [steps, setSteps] = useState<DraftStep[]>([
    { label: 'Deposit', share: '50', rule: 'days_after_issue', days: '7' },
    { label: 'Balance', share: '', rule: 'days_before_event', days: '14' },
  ])
  const [saving, setSaving] = useState(false)

  async function setDefault(id: string) {
    const { error: clear } = await supabase.from('payment_plans').update({ is_default: false }).eq('is_default', true)
    const { error } = clear ? { error: clear } : await supabase.from('payment_plans').update({ is_default: true }).eq('id', id)
    if (error) toast.error('Could not change the default', { description: describeError(error) })
    void queryClient.invalidateQueries({ queryKey: invoiceKeys.plans })
  }

  async function retire(id: string) {
    const { error } = await supabase.from('payment_plans').update({ active: false }).eq('id', id)
    if (error) toast.error('Could not remove the plan', { description: describeError(error) })
    void queryClient.invalidateQueries({ queryKey: invoiceKeys.plans })
  }

  async function create() {
    setSaving(true)
    try {
      const { data, error } = await supabase
        .from('payment_plans')
        .insert({ name: name.trim(), sort_order: (query.data?.length ?? 0) + 1 })
        .select('id')
        .single()
      if (error) throw error
      const { error: stepsError } = await supabase.from('payment_plan_steps').insert(
        steps.map((step, index) => ({
          plan_id: data.id,
          position: index + 1,
          label: step.label.trim(),
          // The last step always takes the rest.
          share: index === steps.length - 1 || step.share.trim() === '' ? null : Number(step.share) / 100,
          due_rule: step.rule,
          offset_days: NEEDS_DAYS.includes(step.rule) ? Number(step.days) || 0 : 0,
        })),
      )
      if (stepsError) throw stepsError
      toast.success('Payment plan added')
      setName('')
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.plans })
    } catch (error) {
      toast.error('Could not add the plan', { description: describeError(error) })
    } finally {
      setSaving(false)
    }
  }

  const shares = steps.slice(0, -1).reduce((sum, step) => sum + (Number(step.share) || 0), 0)
  const valid = name.trim() !== '' && steps.every((step) => step.label.trim() !== '') && shares < 100

  return (
    <div className="max-w-3xl">
      <Section title="Your plans" description="Chosen when a quote is accepted. The default is picked first.">
        <ul className="flex flex-col">
          {(query.data ?? []).map((plan) => (
            <li key={plan.id} className="flex items-start justify-between gap-3 border-b py-2.5">
              <div className="min-w-0">
                <p className="text-[length:var(--text-sm)]">
                  {plan.name}
                  {plan.is_default ? <span className="label ml-2 text-sage">Default</span> : null}
                </p>
                {plan.steps.map((step) => (
                  <p key={step.id} className="text-[length:var(--text-2xs)] text-taupe">
                    {describeStep(step)}
                  </p>
                ))}
              </div>
              <span className="flex shrink-0 gap-1">
                {!plan.is_default ? (
                  <Button variant="ghost" size="sm" onClick={() => void setDefault(plan.id)}>
                    Make default
                  </Button>
                ) : null}
                <Button variant="ghost" size="sm" onClick={() => void retire(plan.id)}>
                  Remove
                </Button>
              </span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Add a plan">
        <div className="flex flex-col gap-4">
          <Field label="Name">
            <Input value={name} placeholder="50% deposit + 50% a month before" onChange={(event) => setName(event.target.value)} />
          </Field>
          {steps.map((step, index) => {
            const last = index === steps.length - 1
            const update = (patch: Partial<DraftStep>) => setSteps((current) => current.map((item, i) => (i === index ? { ...item, ...patch } : item)))
            return (
              <div key={index} className="grid items-end gap-2 sm:grid-cols-[minmax(0,1fr)_7rem_6rem_minmax(0,1fr)_auto]">
                <Field label="Payment">
                  <Input value={step.label} onChange={(event) => update({ label: event.target.value })} />
                </Field>
                <Field label="Share">
                  {last ? (
                    <p className="flex h-[var(--size-control)] items-center text-[length:var(--text-sm)] text-taupe">The rest</p>
                  ) : (
                    <Affixed suffix="%">
                      <Input type="number" min="1" max="99" value={step.share} onChange={(event) => update({ share: event.target.value })} />
                    </Affixed>
                  )}
                </Field>
                <Field label="Days">
                  <Input
                    type="number"
                    min="0"
                    disabled={!NEEDS_DAYS.includes(step.rule)}
                    value={NEEDS_DAYS.includes(step.rule) ? step.days : ''}
                    onChange={(event) => update({ days: event.target.value })}
                  />
                </Field>
                <Field label="Due">
                  <Select value={step.rule} onChange={(event) => update({ rule: event.target.value as DueRule })}>
                    {(Object.keys(RULE_LABEL) as DueRule[]).map((rule) => (
                      <option key={rule} value={rule}>
                        {RULE_LABEL[rule]}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={steps.length <= 1}
                  onClick={() => setSteps((current) => current.filter((_, i) => i !== index))}
                >
                  Remove
                </Button>
              </div>
            )
          })}
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2.5"
              onClick={() => setSteps((current) => [...current.slice(0, -1), { label: 'Instalment', share: '25', rule: 'days_before_event', days: '60' }, ...current.slice(-1)])}
            >
              Add a payment
            </Button>
          </div>
          {shares >= 100 ? <p className="text-[length:var(--text-2xs)] text-clay">The shares before the last payment must add up to less than 100%.</p> : null}
          <Button variant="primary" className="self-start" disabled={!valid} loading={saving} onClick={() => void create()}>
            Add plan
          </Button>
        </div>
      </Section>
    </div>
  )
}
